/**
 * HWPX — reading and text editing for the Korean open standard (§8 Phase 2,
 * §18).
 *
 * HWPX is OWPML (KS X 6101) in a ZIP, which means the part-preserving save
 * built for OOXML works here without modification — and that matters more than
 * convenience. §18 requires Hangul documents to open with usable fidelity and
 * to save without quietly discarding what the editor does not model, and this
 * format carries plenty that it does not model: equations, charts, OLE
 * objects, form controls, distribution signatures. None of those are parsed on
 * the save path, so none of them can be lost there.
 *
 * The `mimetype` entry must stay first and uncompressed for the file to remain
 * recognisable; `repackPackage` preserves entry order and stores that name
 * uncompressed, so a saved HWPX is still detected as one.
 */
import { FORMATS_BY_ID, formatFileSize } from '@moduviewer/core';
import type {
  ExportResult, FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview, ValidationReport,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';
import {
  hasPart, openPackage, partsWithPrefix, readPart, readPartText, repackPackage,
} from '../ooxml/package.js';
import type { OpcPackage } from '../ooxml/package.js';
import {
  applyHwpxParagraphEdits, parseContentHpf, parseHeader, parseSection,
} from './section-xml.js';
import type {
  HwpxBlock, HwpxHeader, HwpxParagraph, HwpxTable,
} from './section-xml.js';

export type {
  CharShape, HwpxBlock, HwpxHeader, HwpxParagraph, HwpxRun, HwpxTable,
  ParaShape, StyleInfo,
} from './section-xml.js';

const CONTENT_HPF = 'Contents/content.hpf';
const HEADER_PART = 'Contents/header.xml';
const PREVIEW_BLOCKS = 40;

export interface HwpxImage {
  readonly part: string;
  readonly bytes: Uint8Array;
  readonly contentType: string;
}

export interface HwpxModel {
  readonly blocks: readonly HwpxBlock[];
  readonly paragraphs: readonly HwpxParagraph[];
  /** Section parts in reading order. */
  readonly sections: readonly string[];
  /** Formatting tables the runs' id references point at. */
  readonly header: HwpxHeader;
  /** Pending text edits, paragraph index → new text. Applied at export (§13). */
  readonly edits: ReadonlyMap<number, string>;
  readonly images: ReadonlyMap<string, HwpxImage>;
  readonly hasEquations: boolean;
  readonly hasObjects: boolean;
  readonly hasSignature: boolean;
  readonly wordCount: number;
}

const IMAGE_TYPES: Readonly<Record<string, string>> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  bmp: 'image/bmp', webp: 'image/webp', tiff: 'image/tiff', tif: 'image/tiff',
  wmf: 'image/wmf', emf: 'image/emf', svg: 'image/svg+xml',
};

/* ------------------------------------------------------------------ *
 * package structure
 * ------------------------------------------------------------------ */

interface Structure {
  readonly sections: string[];
  readonly binaryItems: Map<string, string>;
  readonly header: HwpxHeader;
}

function readStructure(pkg: OpcPackage): Structure {
  const manifest = hasPart(pkg, CONTENT_HPF)
    ? parseContentHpf(readPartText(pkg, CONTENT_HPF))
    : { sections: [], binaryItems: new Map<string, string>() };

  // A spine that names parts the package does not have is worse than no spine:
  // fall back to whatever sections are actually present, in numeric order.
  let sections = manifest.sections.filter((part) => hasPart(pkg, part));
  if (sections.length === 0) {
    sections = pkg.directory.entries
      .map((e) => e.name)
      .filter((n) => /^Contents\/section\d+\.xml$/.test(n))
      .sort((a, b) => sectionNumber(a) - sectionNumber(b));
  }

  // The manifest is not the only place a picture can be named; BinData parts
  // are conventionally `BinData/<id>.<ext>`, so index those too.
  const binaryItems = new Map(manifest.binaryItems);
  for (const entry of pkg.directory.entries) {
    if (!entry.name.startsWith('BinData/')) continue;
    const base = entry.name.slice('BinData/'.length);
    const id = base.slice(0, base.lastIndexOf('.') === -1 ? base.length : base.lastIndexOf('.'));
    if (!binaryItems.has(id)) binaryItems.set(id, entry.name);
  }

  return {
    sections,
    binaryItems,
    header: parseHeader(hasPart(pkg, HEADER_PART) ? readPartText(pkg, HEADER_PART) : ''),
  };
}

function sectionNumber(part: string): number {
  return Number(/section(\d+)\.xml$/.exec(part)?.[1] ?? 0);
}

interface ReadResult {
  blocks: HwpxBlock[];
  paragraphs: HwpxParagraph[];
  truncated: boolean;
}

function readSections(
  pkg: OpcPackage, structure: Structure, maxBlocks: number, signal?: AbortSignal,
): ReadResult {
  const blocks: HwpxBlock[] = [];
  const paragraphs: HwpxParagraph[] = [];
  let truncated = false;

  for (const part of structure.sections) {
    if (signal) throwIfAborted(signal);
    const parsed = parseSection(readPartText(pkg, part), {
      part,
      binaryItems: structure.binaryItems,
      maxBlocks: maxBlocks - blocks.length,
      indexOffset: paragraphs.length,
    });
    blocks.push(...parsed.blocks);
    paragraphs.push(...parsed.paragraphs);
    if (parsed.truncated) { truncated = true; break; }
  }

  return { blocks, paragraphs, truncated };
}

function readImages(
  pkg: OpcPackage, blocks: readonly HwpxBlock[], budgetBytes: number,
): { images: Map<string, HwpxImage>; skipped: string[] } {
  const wanted = new Set<string>();
  const collect = (block: HwpxBlock): void => {
    if (block.kind === 'paragraph') {
      for (const run of block.runs) if (run.kind === 'image' && run.part) wanted.add(run.part);
      return;
    }
    for (const row of block.rows) for (const cell of row) for (const p of cell.paragraphs) collect(p);
  };
  for (const block of blocks) collect(block);

  const images = new Map<string, HwpxImage>();
  const skipped: string[] = [];
  let spent = 0;

  for (const part of wanted) {
    const extension = part.slice(part.lastIndexOf('.') + 1).toLowerCase();
    const contentType = IMAGE_TYPES[extension];
    if (!contentType || !hasPart(pkg, part)) { skipped.push(part); continue; }
    try {
      const bytes = readPart(pkg, part);
      if (spent + bytes.length > budgetBytes) { skipped.push(part); continue; }
      spent += bytes.length;
      images.set(part, { part, bytes, contentType });
    } catch {
      skipped.push(part);
    }
  }
  return { images, skipped };
}

function countWords(paragraphs: readonly HwpxParagraph[]): number {
  let total = 0;
  for (const paragraph of paragraphs) {
    const trimmed = paragraph.text.trim();
    if (trimmed) total += trimmed.split(/\s+/).length;
  }
  return total;
}

/* ------------------------------------------------------------------ *
 * handler
 * ------------------------------------------------------------------ */

export const hwpxHandler: FormatHandler<HwpxModel> = {
  id: 'hwpx',
  formatIds: ['hwpx'],

  async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
    throwIfAborted(ctx.signal);
    const pkg = openPackage(source.bytes);
    const structure = readStructure(pkg);
    if (structure.sections.length === 0) {
      throw new HandlerError('error.damagedOrIncomplete', 'document declares no sections');
    }

    // §11: the first section's opening blocks and nothing more. Hangul also
    // ships a plain-text preview part, but it is a cache that can be stale —
    // reading the real content is not much dearer and cannot be wrong.
    const parsed = readSections(pkg, structure, PREVIEW_BLOCKS);

    return {
      kind: 'pages',
      unitCount: parsed.truncated ? undefined : parsed.paragraphs.length,
      unitKey: 'viewer.paragraph',
      firstContent: {
        blocks: parsed.blocks.slice(0, PREVIEW_BLOCKS),
        header: structure.header,
        more: parsed.truncated,
      },
      warnings: [],
    };
  },

  async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<HwpxModel>> {
    throwIfAborted(ctx.signal);
    const pkg = openPackage(source.bytes);
    const structure = readStructure(pkg);
    if (structure.sections.length === 0) {
      throw new HandlerError('error.damagedOrIncomplete', 'document declares no sections');
    }

    const maxBlocks = Math.max(1, Math.floor(ctx.limits.maxSpreadsheetCells / 8));
    const parsed = readSections(pkg, structure, maxBlocks, ctx.signal);
    if (parsed.truncated) {
      throw new HandlerError(
        'error.unableToRead',
        `document exceeds the block budget of ${maxBlocks}`,
      );
    }

    const { images, skipped } = readImages(pkg, parsed.blocks, ctx.limits.maxFileBytes / 4);

    const objects = parsed.paragraphs.flatMap((p) => p.runs)
      .filter((r) => r.kind === 'object')
      .map((r) => (r.kind === 'object' ? r.what : ''));
    const hasEquations = objects.includes('equation');
    const hasObjects = objects.some((what) => what !== 'equation');
    const hasSignature = pkg.directory.entries.some((e) =>
      e.name.startsWith('DocOptions/') || e.name.includes('Signature'));

    const spec = FORMATS_BY_ID.get(source.formatId);
    const warnings: HandlerWarning[] = [];

    if (hasEquations) {
      warnings.push({
        messageKey: 'error.featuresUnsupported',
        severity: 'degraded',
        detail: 'Equation objects are preserved on save but shown as a placeholder; paragraphs holding one cannot be text-edited (§18).',
      });
    }
    if (hasObjects) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'info',
        detail: `Charts, OLE objects and controls are preserved on save but not rendered (${[...new Set(objects)].join(', ')}).`,
      });
    }
    if (hasSignature) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'caution',
        detail: 'This document carries distribution or signature data. Saving rewrites the package, which may invalidate it (§18).',
      });
    }
    if (structure.header.charShapes.size === 0) {
      warnings.push({
        messageKey: 'error.formattingMayNotBePreserved',
        severity: 'degraded',
        detail: 'Contents/header.xml is missing, so character and paragraph shapes could not be resolved. Text is shown unformatted.',
      });
    }
    if (skipped.length > 0) {
      warnings.push({
        messageKey: 'error.featuresUnsupported',
        severity: 'degraded',
        detail: `${skipped.length} pictures could not be decoded for display; they are preserved on save.`,
      });
    }
    if (source.sizeBytes > 20 * 1024 * 1024) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'info',
        detail: `large document (${formatFileSize('en', source.sizeBytes)})`,
      });
    }

    return {
      formatId: source.formatId,
      model: {
        blocks: parsed.blocks,
        paragraphs: parsed.paragraphs,
        sections: structure.sections,
        header: structure.header,
        edits: new Map(),
        images,
        hasEquations,
        hasObjects,
        hasSignature,
        wordCount: countWords(parsed.paragraphs),
      },
      readOnly: ctx.assessment.forceReadOnly,
      editLevel: spec?.edit ?? 'basic-edit',
      lossOnSave: spec?.lossOnSave ?? [],
      warnings,
    };
  },

  async exportBytes(
    model: HwpxModel, source: FileSource, ctx: HandlerContext,
  ): Promise<ExportResult> {
    throwIfAborted(ctx.signal);
    const pkg = openPackage(source.bytes);
    const replacements = new Map<string, Uint8Array>();
    const dropped: string[] = [];

    if (model.edits.size > 0) {
      const structure = readStructure(pkg);
      // Re-parse from the original bytes so the spans belong to this XML.
      const parsed = readSections(pkg, structure, Number.MAX_SAFE_INTEGER);

      // Group edits by the section part that holds them: each part is rewritten
      // once, and parts with no edits are never touched at all.
      const byPart = new Map<string, { index: number; text: string }[]>();
      for (const [index, text] of model.edits) {
        const paragraph = parsed.paragraphs.find((p) => p.index === index);
        if (!paragraph) {
          throw new HandlerError('error.unableToRead', `no paragraph at index ${index}`);
        }
        const list = byPart.get(paragraph.part) ?? [];
        list.push({ index, text });
        byPart.set(paragraph.part, list);
      }

      for (const [part, edits] of byPart) {
        const xml = readPartText(pkg, part);
        const inPart = parsed.paragraphs.filter((p) => p.part === part);
        const result = applyHwpxParagraphEdits(xml, inPart, edits);
        if (result.failed.length > 0) {
          throw new HandlerError(
            'error.unableToRead',
            `could not rewrite paragraphs in ${part}: ${result.failed.join(', ')}`,
          );
        }
        dropped.push(...result.dropped);
        replacements.set(part, new TextEncoder().encode(result.xml));
      }
    }

    const repacked = repackPackage(pkg, replacements);

    return {
      bytes: repacked.bytes,
      suggestedName: source.filename,
      formatId: source.formatId,
      dropped,
    };
  },

  async validateExport(
    bytes: Uint8Array, model: HwpxModel, ctx: HandlerContext,
  ): Promise<ValidationReport> {
    throwIfAborted(ctx.signal);
    const issues: string[] = [];
    let reopened = false;
    let contentPreserved = false;

    try {
      const pkg = openPackage(bytes);
      const structure = readStructure(pkg);
      const parsed = readSections(pkg, structure, Number.MAX_SAFE_INTEGER);
      reopened = true;

      // A HWPX whose mimetype entry moved or got compressed stops being
      // recognisable as one, which is a silent corruption from the user's side.
      const first = pkg.directory.entries[0];
      if (first?.name !== 'mimetype') {
        issues.push('the mimetype entry is no longer first in the package');
      } else if (first.method !== 0) {
        issues.push('the mimetype entry is no longer stored uncompressed');
      }

      if (structure.sections.join('|') !== model.sections.join('|')) {
        issues.push(`section order changed: ${model.sections.join(', ')} → ${structure.sections.join(', ')}`);
      }
      if (parsed.paragraphs.length !== model.paragraphs.length) {
        issues.push(`paragraph count changed: ${model.paragraphs.length} → ${parsed.paragraphs.length}`);
      }

      for (const [i, expected] of model.paragraphs.entries()) {
        const actual = parsed.paragraphs[i];
        if (!actual) { issues.push(`paragraph ${i} disappeared`); continue; }
        const edited = model.edits.get(expected.index);
        const want = edited ?? expected.originalText;
        if (actual.text !== want) {
          issues.push(`paragraph ${i}: ${JSON.stringify(want)} → ${JSON.stringify(actual.text)}`);
        }
        // The style reference is what carries the formatting; losing it is
        // exactly the failure a naive rebuild would produce.
        if (actual.styleId !== expected.styleId || actual.paraPrId !== expected.paraPrId) {
          issues.push(`paragraph ${i} lost its shape references`);
        }
      }

      if (!hasPart(pkg, HEADER_PART) && model.header.charShapes.size > 0) {
        issues.push('Contents/header.xml was lost during save');
      }
      for (const part of model.images.keys()) {
        if (!hasPart(pkg, part)) issues.push(`picture part missing: ${part}`);
      }

      contentPreserved = issues.length === 0;
    } catch (err) {
      issues.push(`could not reopen the exported document: ${(err as Error).message}`);
    }

    return { ok: reopened && contentPreserved, reopened, contentPreserved, issues };
  },
};

/* ------------------------------------------------------------------ *
 * editing helpers
 * ------------------------------------------------------------------ */

/** Queue a paragraph's new text. Nothing is written until export (§13). */
export function setHwpxParagraphText(
  model: HwpxModel, index: number, text: string,
): HwpxModel {
  const paragraph = model.paragraphs.find((p) => p.index === index);
  if (!paragraph) throw new HandlerError('error.unableToRead', `no paragraph at index ${index}`);
  if (!paragraph.editable) {
    throw new HandlerError(
      'error.featuresUnsupported',
      `paragraph ${index} contains content that text editing cannot preserve`,
    );
  }

  const edits = new Map(model.edits);
  if (text === paragraph.originalText) edits.delete(index);
  else edits.set(index, text);

  const updated: HwpxParagraph = {
    ...paragraph,
    text,
    runs: [{ kind: 'text', text, charPrId: paragraph.charPrId }],
  };

  const replace = (block: HwpxBlock): HwpxBlock => {
    if (block.kind === 'paragraph') return block.index === index ? updated : block;
    return {
      ...block,
      rows: block.rows.map((row) => row.map((cell) => ({
        ...cell,
        paragraphs: cell.paragraphs.map((p) => (p.index === index ? updated : p)),
      }))),
    };
  };

  const paragraphs = model.paragraphs.map((p) => (p.index === index ? updated : p));
  return {
    ...model,
    edits,
    blocks: model.blocks.map(replace),
    paragraphs,
    wordCount: countWords(paragraphs),
  };
}

/** Heading level for a paragraph, resolved through the style table. */
export function headingLevelOf(model: HwpxModel, paragraph: HwpxParagraph): number | null {
  return paragraph.styleId
    ? model.header.styles.get(paragraph.styleId)?.headingLevel ?? null
    : null;
}

/** Plain text of the whole document, for search and for copying out. */
export function hwpxToPlainText(model: HwpxModel): string {
  const lines: string[] = [];
  const walk = (block: HwpxBlock): void => {
    if (block.kind === 'paragraph') { lines.push(block.text); return; }
    for (const row of block.rows) {
      lines.push(row.map((cell) => cell.paragraphs.map((p) => p.text).join(' ')).join('\t'));
    }
  };
  for (const block of model.blocks) walk(block);
  return lines.join('\n');
}

/** Parts that exist but are neither read nor rendered, for the §31 screen. */
export function unrenderedParts(bytes: Uint8Array): string[] {
  const pkg = openPackage(bytes);
  return [
    ...partsWithPrefix(pkg, 'Scripts/'),
    ...partsWithPrefix(pkg, 'DocOptions/'),
    ...partsWithPrefix(pkg, 'Custom/'),
  ];
}
